import React, { useState } from "react";
import { bool, object, string } from "yup";
import Image from "next/image";
import styled from "@emotion/styled";
import { toast } from "react-toastify";
import { InputVariantTypes, User } from "@ecologi/shared";
import { useForm } from "@ecologi/react-components/src/utils";
import { TextField } from "../TextField";
import { Button } from "../Button";
import { Wrapper } from "../Wrapper";
import { Typography } from "../Typography";
import { Checkbox } from "../Checkbox";
import { EcologiTheme } from "../theme";

const MAX_FEEDBACK_LENGTH = 600;

export const TEXT_FIELD_LABEL_TEXT = "Here's what I am thinking...";
export const EMPTY_TEXT_FIELD_ERROR_TEXT =
  "This field is empty. Enter your feedback text.";
export const CHECKBOX_LABEL_TEXT = "It’s ok to email me about this";
export const CTA_BUTTON_TEXT = "improve this page";
export const SUBMIT_BUTTON_TEXT = "Submit idea";
export const CANCEL_BUTTON_TEXT = "Cancel";
export const CLOSE_BUTTON_TEXT = "Close";
export const REENTRY_BUTTON_TEXT = "Suggest another idea";
export const SUCCESS_MESSAGE_HEADING_TEXT = "Thank you!";

interface BackgroundVariantProps {
  variant?: FeedbackFormVariant;
}

export type FeedbackFormProps = {
  currentUser?: User;
  currentPath: string;
  onSubmit: (values: FeedbackFormFields) => Promise<void>;
} & BackgroundVariantProps;

export interface FeedbackFormFields {
  contactable: boolean;
  feedback: string;
  submissionDate: string;
  submissionPath: string;
  userId?: string;
}

export enum FeedbackFormVariant {
  PEARL = "PEARL",
  GREYBOY = "GREYBOY",
  SNOWDROP = "SNOWDROP",
}

enum FormStatus {
  Closed,
  Open,
  Complete,
}

// TODO: Use tints once available in design system
const mainBackgroundMapper = {
  [FeedbackFormVariant.PEARL]: (theme: EcologiTheme) => "#FAF9F8",
  [FeedbackFormVariant.GREYBOY]: (theme: EcologiTheme) => "#F9F9F9",
  [FeedbackFormVariant.SNOWDROP]: (theme: EcologiTheme) => "#F2F2F2",
};

const ctaBackgroundMapper = {
  [FeedbackFormVariant.PEARL]: (theme: EcologiTheme) =>
    theme.colors.mushroomLight,
  [FeedbackFormVariant.GREYBOY]: (theme: EcologiTheme) => theme.colors.mist,
  [FeedbackFormVariant.SNOWDROP]: (theme: EcologiTheme) =>
    theme.colors.snowdrop,
};

const FeedbackFormWrapper = styled.div<BackgroundVariantProps>`
  background-color: ${(props) =>
    props.variant && mainBackgroundMapper[props.variant]
      ? mainBackgroundMapper[props.variant](props.theme)
      : mainBackgroundMapper[FeedbackFormVariant.PEARL](props.theme)};
`;

const CtaWrapper = styled.div<BackgroundVariantProps>`
  background-color: ${(props) =>
    props.variant && ctaBackgroundMapper[props.variant]
      ? ctaBackgroundMapper[props.variant](props.theme)
      : ctaBackgroundMapper[FeedbackFormVariant.PEARL](props.theme)};

  padding: 60px 0;
  text-align: center;
`;

const FormWrapper = styled(Wrapper)`
  padding-top: 30px;
  padding-bottom: 65px;

  @media ${(props) => props.theme.bp.mobileUp} {
    padding-top: 30px;
    padding-bottom: 80px;
  }
`;

const FeedbackTextField = styled(TextField)`
  margin-bottom: 20px;
`;

const FeedbackCheckbox = styled(Checkbox)`
  padding: 0;
  margin-bottom: 30px;
`;

const SuccessMessageWrapper = styled(Wrapper)`
  padding-top: 90px;
  padding-bottom: 90px;
  text-align: center;
`;

const SuccessMessageHeading = styled(Typography)`
  margin: 10px 0 15px;
`;

const SuccessMessageSubheading = styled(Typography)`
  margin-bottom: 20px;
`;

const FormActions = styled.div`
  display: flex;
  justify-content: space-between;
`;

const CtaButton = styled(Button)`
  background: none;
  border: none;
  padding: 0;
  text-decoration: underline;
  cursor: pointer;
  font-weight: inherit;
  font-size: inherit;
`;

const CancelButton = styled(Button)`
  border-color: ${(props) => props.theme.colors.charcoal};
`;

const SuccessMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const CloseButton = styled(Button)`
  border-color: ${(props) => props.theme.colors.charcoal};
`;

const ReentryButton = styled(Button)`
  text-decoration: underline;
  margin-bottom: 20px;

  @media ${(props) => props.theme.bp.mobileUp} {
    margin-bottom: 40px;
  }
`;

export const FeedbackForm = ({
  currentUser,
  currentPath,
  onSubmit,
  variant,
}: FeedbackFormProps) => {
  const [status, setStatus] = useState(FormStatus.Closed);

  const schema = object().shape({
    contactable: bool().required().default(false),
    feedback: string()
      .required(EMPTY_TEXT_FIELD_ERROR_TEXT)
      .max(MAX_FEEDBACK_LENGTH)
      .default(""),
    submissionDate: string().required().default(new Date().toISOString()),
    submissionPath: string().required().default(currentPath),
    userId: string().optional().default(currentUser?.userId),
  });

  const {
    values,
    setFieldValue,
    handleSubmit,
    isSubmitting,
    errors,
    touched,
    resetForm,
  } = useForm<FeedbackFormFields>({
    validationSchema: schema,
    initialValues: schema.getDefaultFromShape(),
    onSubmit: async (values) => {
      try {
        await onSubmit({ ...values, submissionDate: new Date().toISOString() });
        setStatus(FormStatus.Complete);
        resetForm();
      } catch (error) {
        toast.error("An error has occurred. Please try again.");
      }
    },
  });

  return (
    <FeedbackFormWrapper variant={variant}>
      {(status === FormStatus.Closed || status === FormStatus.Open) && (
        <CtaWrapper variant={variant}>
          <Wrapper>
            <Typography use="subtitle2">
              {"Help us "}
              <CtaButton
                text={CTA_BUTTON_TEXT}
                textOnly
                onClick={() => setStatus(FormStatus.Open)}
              />
              {" 💚"}
            </Typography>
          </Wrapper>
        </CtaWrapper>
      )}

      {status === FormStatus.Open && (
        <FormWrapper isExtraSmall>
          <form onSubmit={handleSubmit}>
            <FeedbackTextField
              value={values.feedback}
              label={TEXT_FIELD_LABEL_TEXT}
              name="feedback"
              isMultiline
              maxLength={MAX_FEEDBACK_LENGTH}
              showCharacterCount
              autoFocus
              onChange={(event) =>
                setFieldValue("feedback", event.target.value)
              }
              isDisabled={isSubmitting}
              isInvalid={touched.feedback && errors.feedback !== undefined}
              errorText={errors.feedback}
              variant={InputVariantTypes.SNOWDROP}
            />
            {currentUser && (
              <FeedbackCheckbox
                label={CHECKBOX_LABEL_TEXT}
                name="contactable"
                isDisabled={isSubmitting}
                onChange={(isSelected) =>
                  setFieldValue("contactable", isSelected)
                }
              >
                It’s ok to email me about this 👍
              </FeedbackCheckbox>
            )}
            <FormActions>
              <Button
                text={SUBMIT_BUTTON_TEXT}
                submit
                darkGreen
                disabled={isSubmitting}
                loading={isSubmitting}
              />
              <CancelButton
                text={CANCEL_BUTTON_TEXT}
                textOnly
                border
                onClick={() => {
                  resetForm();
                  setStatus(FormStatus.Closed);
                }}
                disabled={isSubmitting}
              />
            </FormActions>
          </form>
        </FormWrapper>
      )}

      {status === FormStatus.Complete && (
        <FeedbackFormSuccessMessage
          onClose={() => setStatus(FormStatus.Closed)}
          onReentry={() => setStatus(FormStatus.Open)}
        />
      )}
    </FeedbackFormWrapper>
  );
};

interface FeedbackFormSuccessMessageProps {
  onClose: () => void;
  onReentry: () => void;
}

const FeedbackFormSuccessMessage = ({
  onReentry,
  onClose,
}: FeedbackFormSuccessMessageProps) => (
  <SuccessMessageWrapper>
    <Image
      src="/images/app/sunflower.svg"
      alt="Sunflower"
      width={327}
      height={194}
      unoptimized
    />
    <SuccessMessage>
      <SuccessMessageHeading use="featureTitle">
        {SUCCESS_MESSAGE_HEADING_TEXT}
      </SuccessMessageHeading>
      <SuccessMessageSubheading use="body1">
        We love to hear your ideas. Keep them coming!
      </SuccessMessageSubheading>
      <ReentryButton
        text={`${REENTRY_BUTTON_TEXT} 💚`}
        textOnly
        onClick={onReentry}
      />
      <CloseButton text={CLOSE_BUTTON_TEXT} textOnly border onClick={onClose} />
    </SuccessMessage>
  </SuccessMessageWrapper>
);
